import type { ItemStack } from "../engine/types/items.js";

/**
 * I08 — the magical camp chest. One shared stash with **infinite
 * capacity**, accessible from any camp. Contents persist across floors
 * and travel with the save file (P01 serializers pick this up via
 * `toJSON` / `loadJSON`).
 *
 * API shape mirrors `LootPile` deliberately: deposit / withdraw / list.
 * The stash is weight-agnostic — storing a plate-mail set is free — so
 * there's no capacity budget to track. Ordering is preserved so the
 * UI listing stays stable.
 */
const stacks: ItemStack[] = [];

export const CampStashStore = {
  list(): readonly ItemStack[] {
    return stacks;
  },

  /**
   * Deposit a stack. Stackables merge with any existing entry whose
   * item id matches; non-stackables append.
   */
  deposit(stack: ItemStack): void {
    if (stack.quantity <= 0) return;
    if (stack.item.stackable) {
      const idx = stacks.findIndex((s) => s.item.id === stack.item.id);
      if (idx >= 0) {
        const existing = stacks[idx]!;
        stacks[idx] = { item: existing.item, quantity: existing.quantity + stack.quantity };
        return;
      }
    }
    stacks.push(stack);
  },

  /**
   * Withdraw up to `quantity` of `itemId`. Returns the amount actually
   * removed (may be less than requested).
   */
  withdraw(itemId: string, quantity: number): number {
    if (quantity <= 0) return 0;
    const idx = stacks.findIndex((s) => s.item.id === itemId);
    if (idx < 0) return 0;
    const stack = stacks[idx]!;
    const take = Math.min(quantity, stack.quantity);
    if (take === stack.quantity) {
      stacks.splice(idx, 1);
    } else {
      stacks[idx] = { item: stack.item, quantity: stack.quantity - take };
    }
    return take;
  },

  /** Test/reset helper. Also called by `loadJSON` before restoring. */
  clear(): void {
    stacks.length = 0;
  },

  toJSON(): readonly ItemStack[] {
    return stacks.map((s) => ({ ...s }));
  },

  loadJSON(input: readonly ItemStack[]): void {
    stacks.length = 0;
    for (const s of input) stacks.push({ ...s });
  },
};
